import React from 'react';

const TomkoPlusTerms = () => {
  return (
    <div style={{ padding: '2rem', paddingTop: '8rem', maxWidth: '900px', margin: '0 auto' }}>
      <h1>Terms of Use (EULA) for Tomko Plus</h1>
      <p><strong>Last updated:</strong> December 2025</p>

      <p>
        These Terms of Use ("Terms", "Agreement") constitute a legally binding agreement between you ("User", "you", or "your") and the Company (referred to as "the Company", "We", "Us" or "Our") governing your use of the Tomko Plus subscription service ("Service", "Subscription").
      </p>

      <p>
        By subscribing to Tomko Plus, you agree to be bound by these Terms. If you do not agree to these Terms, please do not subscribe to or use Tomko Plus.
      </p>

      <h2>1. Subscription Service</h2>
      <h3>1.1 Service Description</h3>
      <p>
        Tomko Plus is an auto-renewable subscription service that provides access to premium features of the Tomko application, including but not limited to:
      </p>
      <ul>
        <li>Unlimited life in exercises</li>
        <li>Unlimited access to all lessons</li>
        <li>Early access to new courses</li>
        <li>Additional premium content and features as may be added from time to time</li>
      </ul>

      <h3>1.2 Subscription Title</h3>
      <p>
        <strong>Title of Auto-Renewing Subscription:</strong> Tomko Plus
      </p>

      <h3>1.3 Subscription Length</h3>
      <p>
        Tomko Plus subscriptions are available in the following durations:
      </p>
      <ul>
        <li><strong>Monthly Subscription:</strong> 1 month (renews monthly)</li>
        <li><strong>Annual Subscription:</strong> 12 months (renews annually)</li>
      </ul>
      <p>
        The specific subscription length available to you may vary depending on your region and the platform (Google Play Store or Apple App Store) through which you subscribe.
      </p>

      <h3>1.4 Subscription Price</h3>
      <p>
        <strong>Price of Subscription:</strong> The subscription price for Tomko Plus is displayed in the application and in the respective app store (Google Play Store or Apple App Store) before purchase. Prices may vary by region and are subject to change.
      </p>
      <p>
        <strong>Price per Unit:</strong> The price per month or per year, as applicable, is clearly displayed at the time of subscription purchase. For annual subscriptions, the price represents the total cost for 12 months of service.
      </p>
      <p>
        All prices are displayed in the local currency applicable to your region and payment method.
      </p>

      <h2>2. Auto-Renewal</h2>
      <h3>2.1 Automatic Renewal</h3>
      <p>
        Tomko Plus subscriptions automatically renew at the end of each subscription period unless you cancel your subscription before the renewal date. By subscribing, you authorize the Company and the respective platform (Google Play Store or Apple App Store) to charge your payment method automatically for the renewal period.
      </p>

      <h3>2.2 Renewal Timing</h3>
      <p>
        Your subscription will automatically renew at the end of the current subscription period. The renewal will occur:
      </p>
      <ul>
        <li><strong>For Monthly Subscriptions:</strong> At the end of each 1-month period</li>
        <li><strong>For Annual Subscriptions:</strong> At the end of each 12-month period</li>
      </ul>
      <p>
        You will be charged the then-current subscription price for the renewal period. The specific timing for when you must cancel to avoid renewal, as well as when your account will be charged for renewal, are specified in the respective subscription plans provided by Google Play Store and Apple App Store.
      </p>

      <h3>2.3 Cancellation of Auto-Renewal</h3>
      <p>
        You may cancel your subscription at any time to prevent automatic renewal. Cancellation must be completed before the renewal date to avoid being charged for the next subscription period. To cancel your subscription:
      </p>
      <ul>
        <li><strong>Android:</strong> Open Google Play Store → Menu → Subscriptions → Select Tomko Plus → Cancel Subscription</li>
        <li><strong>iOS:</strong> Open Settings → [Your Name] → Subscriptions → Select Tomko Plus → Cancel Subscription</li>
      </ul>
      <p>
        Cancellation will take effect at the end of your current subscription period. You will continue to have access to Tomko Plus features until the end of the period for which you have already paid.
      </p>

      <h2>3. Payment and Billing</h2>
      <h3>3.1 Payment Processing</h3>
      <p>
        All subscription payments are processed through Google Play Store (for Android users) or Apple App Store (for iOS users). The Company does not directly process payment information. All payment transactions are handled by Google or Apple in accordance with their respective terms of service and privacy policies.
      </p>

      <h3>3.2 Payment Method</h3>
      <p>
        Subscription fees are charged to your Google Play Store account (for Android) or Apple ID account (for iOS), depending on your device platform. You are responsible for maintaining a valid payment method on file with the respective platform.
      </p>

      <h3>3.3 Failed Payments</h3>
      <p>
        If payment fails for any reason, the platform (Google Play Store or Apple App Store) may attempt to process the payment again. If payment continues to fail, your subscription may be suspended or cancelled. During any grace period provided by the platform, you may continue to have access to Tomko Plus features as determined by the platform's policies.
      </p>

      <h2>4. Free Trials</h2>
      <h3>4.1 Trial Period</h3>
      <p>
        If a free trial is offered for Tomko Plus, it will be clearly indicated at the time of subscription. Free trials automatically convert to paid subscriptions unless cancelled before the trial period ends.
      </p>

      <h3>4.2 Trial Conversion</h3>
      <p>
        The specific terms and timing for free trial conversion and billing are specified in the respective subscription plans provided by Google Play Store and Apple App Store. Please refer to your subscription plan details in the Google Play Store or Apple App Store for specific free trial information applicable to your subscription.
      </p>
      <p>
        If you do not cancel before the trial period ends, you will be charged the subscription fee and your subscription will begin automatically.
      </p>

      <h2>5. Price Changes</h2>
      <h3>5.1 Right to Modify Prices</h3>
      <p>
        We reserve the right to modify subscription prices at any time. The specific notification requirements and advance notice periods for price changes are specified in the respective subscription plans provided by Google Play Store and Apple App Store.
      </p>

      <h3>5.2 Effect of Price Changes</h3>
      <p>
        Price changes will not affect your current subscription period but will apply to subsequent renewal periods. If you do not agree to a price increase, you may cancel your subscription before the price change takes effect.
      </p>

      <h2>6. Refunds</h2>
      <h3>6.1 No Refund Policy</h3>
      <p>
        <strong>No refunds are available for Tomko Plus subscriptions.</strong> All subscription purchases are final. Once you have purchased a subscription, you will not be eligible for a refund, regardless of whether you have used the service or not.
      </p>

      <h3>6.2 Cancellation and Access</h3>
      <p>
        If you cancel your subscription, you will continue to have access to Tomko Plus until the end of the current billing period for which you have already paid, but you will not receive a refund for any remaining time in that period.
      </p>

      <h3>6.3 Platform Refund Policies</h3>
      <p>
        This no-refund policy applies to all Tomko Plus subscriptions purchased through Google Play Store and Apple App Store. The Company does not process refunds, and refund requests submitted through Google Play Store or Apple App Store will not be honored, except as may be required by applicable law or platform policies.
      </p>

      <h2>7. User Obligations</h2>
      <h3>7.1 Account Responsibility</h3>
      <p>
        You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
      </p>

      <h3>7.2 Acceptable Use</h3>
      <p>
        You agree to use Tomko Plus only for lawful purposes and in accordance with these Terms. You agree not to:
      </p>
      <ul>
        <li>Use the Service in any way that violates any applicable law or regulation</li>
        <li>Attempt to gain unauthorized access to the Service or any related systems or networks</li>
        <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
        <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
        <li>Share your account credentials with others or allow others to access your account</li>
        <li>Use the Service to transmit any harmful, offensive, or illegal content</li>
      </ul>

      <h2>8. Service Availability</h2>
      <h3>8.1 Service Modifications</h3>
      <p>
        We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice. We may also impose limits on certain features or restrict your access to parts or all of the Service without notice or liability.
      </p>

      <h3>8.2 Service Interruptions</h3>
      <p>
        We do not guarantee that the Service will be available at all times or that it will be free from errors, interruptions, or delays. We are not liable for any loss or damage resulting from service interruptions or unavailability.
      </p>

      <h2>9. Intellectual Property</h2>
      <h3>9.1 Ownership</h3>
      <p>
        The Service, including all content, features, and functionality, is owned by the Company and is protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works based on the Service without our express written permission.
      </p>

      <h3>9.2 License</h3>
      <p>
        Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use Tomko Plus for your personal, non-commercial use.
      </p>

      <h2>10. Limitation of Liability</h2>
      <h3>10.1 Disclaimer of Warranties</h3>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
      </p>

      <h3>10.2 Limitation of Liability</h3>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE COMPANY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
      </p>

      <h2>11. Subscription Account</h2>
      <h3>11.1 Platform Account</h3>
      <p>
        Your subscription is tied to your Google Play Store account (for Android) or Apple ID (for iOS). If you switch devices but use the same platform account, your subscription will be available on the new device. Subscriptions cannot be transferred between different platform accounts (e.g., from Google Play to Apple App Store).
      </p>

      <h3>11.2 Multiple Devices</h3>
      <p>
        You may use your Tomko Plus subscription on multiple devices, provided that all devices are associated with the same platform account (Google Play Store account or Apple ID).
      </p>

      <h2>12. Termination</h2>
      <h3>12.1 Termination by You</h3>
      <p>
        You may terminate your subscription at any time by canceling through your device's subscription settings as described in Section 2.3. Your access to Tomko Plus will continue until the end of the current subscription period for which you have already paid.
      </p>

      <h3>12.2 Termination by Us</h3>
      <p>
        We may terminate or suspend your subscription immediately, without prior notice, if you breach these Terms or engage in any fraudulent, abusive, or illegal activity. Upon termination, your right to use the Service will immediately cease.
      </p>

      <h2>13. Changes to These Terms</h2>
      <h3>13.1 Modification of Terms</h3>
      <p>
        We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the new Terms on this page and updating the "Last updated" date at the top of this document.
      </p>

      <h3>13.2 Continued Use</h3>
      <p>
        Your continued use of Tomko Plus after any changes to these Terms constitutes your acceptance of the modified Terms. If you do not agree to the modified Terms, you must cancel your subscription and stop using the Service.
      </p>

      <h2>14. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of Mongolia, without regard to its conflict of law provisions. Any disputes arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Mongolia.
      </p>

      <h2>15. Severability</h2>
      <p>
        If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
      </p>

      <h2>16. Entire Agreement</h2>
      <p>
        These Terms, together with our Privacy Policy, constitute the entire agreement between you and the Company regarding your use of Tomko Plus and supersede all prior agreements and understandings.
      </p>

      <h2>17. Contact Us</h2>
      <p>
        If you have any questions about these Terms of Use, you can contact us:
      </p>
      <ul>
        <li>By visiting our Facebook page: <a href="https://www.facebook.com/tomkomn/" target="_blank" rel="noopener noreferrer">Facebook Profile</a></li>
        <li>By email: contact@gobicraft.com</li>
        {/* <li>By phone: +976 8070-8476</li> */}
      </ul>

      <h2>18. Acknowledgment</h2>
      <p>
        By subscribing to Tomko Plus, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
      </p>
    </div>
  );
};

export default TomkoPlusTerms;





